import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus';
import { throttle } from 'lodash';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { route } from 'ziggy-js';

type NotificationItem = {
    id: string | number;
} & Record<string, unknown>;

type Paginator<T> = {
    data: T[];
    meta?: {
        total?: number;
        unread_count?: number;
        next_cursor?: string | null;
    } & Record<string, unknown>;
    links?: Record<string, unknown>;
} & Record<string, unknown>;

export const useNotificationsStore = defineStore('notifications', () => {
    const notifications = ref<Paginator<NotificationItem> | null>(null);
    const isLoading = ref(false);

    const unreadCount = computed<number>(() => {
        if (!notifications.value) {
            return 0;
        }
        const meta = notifications.value.meta ?? {};
        if (typeof meta.unread_count === 'number') {
            return meta.unread_count;
        }
        if (typeof meta.total === 'number') {
            return meta.total;
        }
        return Array.isArray(notifications.value.data) ? notifications.value.data.length : 0;
    });

    const counter = computed<string | number>(() => {
        const count = unreadCount.value ?? 0;
        return count > 9 ? '9+' : count;
    });

    const removeReadNotification = (notification: NotificationItem): void => {
        if (!notifications.value || !Array.isArray(notifications.value.data)) return;
        notifications.value.data = notifications.value.data.filter((n) => n.id !== notification.id);

        const meta = notifications.value.meta ?? {};
        if (typeof meta.unread_count === 'number') {
            meta.unread_count = Math.max(0, meta.unread_count - 1);
        } else if (typeof meta.total === 'number') {
            meta.total = Math.max(0, meta.total - 1);
        }
        notifications.value.meta = meta;
    };

    const loadUnreadNotifications = (): void => {
        if (isLoading.value) return;

        const params: Record<string, unknown> = {};
        const nextCursor = notifications.value?.meta?.next_cursor;
        if (nextCursor) {
            params.cursor = nextCursor;
        }

        isLoading.value = true;
        $http
            .get(route('notifications.index'), { params })
            .then((resp) => {
                const incoming = resp?.data;
                if (!incoming) return;

                if (notifications.value && Array.isArray(notifications.value.data)) {
                    notifications.value.meta = incoming.meta;
                    notifications.value.links = incoming.links;
                    const existingIds = new Set(notifications.value.data.map((n) => n.id));
                    const nextItems = Array.isArray(incoming.data) ? incoming.data : [];
                    const deduped = nextItems.filter((n: NotificationItem) => !existingIds.has(n.id));
                    notifications.value.data = [...notifications.value.data, ...deduped];
                } else {
                    notifications.value = incoming;
                }

                if ((incoming?.data?.length ?? 0) === 0 && !notifications.value?.data?.length) {
                    // no-op: component can choose to hide dropdown if desired
                }
            })
            .catch(() => {
                if (notifications.value?.data) {
                    notifications.value.data = [];
                }
            })
            .finally(() => {
                isLoading.value = false;
            });
    };

    const throttleLoadUnreadNotifications = throttle(function () {
        loadUnreadNotifications();
    }, 60000);

    const markAllAsRead = (): void => {
        isLoading.value = true;
        $http
            .post(route('notifications.mark_as_read'))
            .then(() => {
                notifications.value = null;
            })
            .finally(() => {
                isLoading.value = false;
                setTimeout(() => {
                    loadUnreadNotifications();
                }, 1000);
            });
    };

    // Lifecycle wiring for external triggers
    const start = (): void => {
        eventBus.$on(events.updateNotifications, loadUnreadNotifications);
        eventBus.$on(events.clockUpdated, () => {
            throttleLoadUnreadNotifications();
        });
        window.addEventListener('focus', loadUnreadNotifications);
    };

    const stop = (): void => {
        eventBus.$off([events.updateNotifications, events.clockUpdated]);
        window.removeEventListener('focus', loadUnreadNotifications);
    };

    return {
        // state
        notifications,
        isLoading,
        // getters
        unreadCount,
        counter,
        // actions
        loadUnreadNotifications,
        markAllAsRead,
        removeReadNotification,
        start,
        stop,
    };
});
